/**
 * Prayer lifecycle actions. Deletion is an ordinary durable tombstone and
 * undo is an explicit restore mutation; neither path physically deletes a
 * prayer or writes an ordinary table directly. Keepsake safety consumes the
 * same transactional prayer fence advanced by the lifecycle store.
 */

import {
  authorDeleteMutation,
  getLocalContentEntity,
  authorExplicitPrayerRestore,
  type ContentLifecycleAuthExpectation,
} from '@/lib/content-lifecycle/store';
import {
  getCurrentContentLifecycleContext,
  syncContentLifecycle,
} from '@/lib/content-lifecycle/sync';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import { completePrayerDocument } from '@/lib/content-lifecycle/contracts';
import { assertCoreWriteAllowed } from '@/lib/core-cutover';
import { useAdiaStore } from '@/lib/store';
import { prayerEditorAuth } from '@/lib/prayer-authoring';
import type { DurablePendingContentTupleV1 } from '@/types/content-lifecycle';
import type { Prayer } from '@/types/prayer';

export type ProtectedActionCode =
  | 'policy-unavailable'
  | 'not-signed-in'
  | 'stale-policy'
  | 'ownership-review-required'
  | 'cannot-restore-physical-delete'
  | 'not-found'
  | 'try-again';

export type ProtectedActionResult =
  | { ok: true; pending: boolean }
  | { ok: false; code: ProtectedActionCode };

function dispatchLifecycleSync(): void {
  void syncContentLifecycle().catch(() => {
    // Local-first: the durable tuple remains pending for the shared reconnect
    // worker. The caller receives pending=true and the UI stays honest.
  });
}

const undoTickets = new WeakMap<Prayer, { tuple: DurablePendingContentTupleV1; auth: ContentLifecycleAuthExpectation }>();

/**
 * RU item 1 (2026-10-20): whether the ordinary Delete (deletePrayerProtected)
 * can work for this prayer. It cannot for a pending prayer that has a recording
 * (`pendingTranscriptionId`) and no content-lifecycle entry: such a prayer was
 * never synced (rule #24), and authorDeleteMutation refuses it because there is
 * no accepted document to tombstone, so deletePrayerProtected would only ever
 * answer `try-again`. Its removal is the recovery Remove (discardFailedAudio)
 * where the prayer page offers it. Decided from the durable lifecycle entry of
 * the signed-in owner, never from offline or sync state; every other prayer,
 * including one saved on this device and still waiting to sync, keeps Delete.
 * Reads only.
 */
export async function ordinaryDeleteSupported(prayer: Prayer): Promise<boolean> {
  if (!prayer.pendingTranscriptionId) return true;
  const ownerId = getAuthUserId();
  // Signed out: unchanged, Delete reports `not-signed-in` itself.
  if (!ownerId) return true;
  return (await getLocalContentEntity(ownerId, 'prayer', prayer.id)) !== null;
}

export async function deletePrayerProtected(prayer: Prayer): Promise<ProtectedActionResult> {
  assertCoreWriteAllowed('content-lifecycle:prayer:delete');
  try {
    const auth = prayerEditorAuth();
    const context = await getCurrentContentLifecycleContext(auth);
    const tuple = await authorDeleteMutation(context, 'prayer', prayer.id, new Date().toISOString(), prayer);
    undoTickets.set(prayer, { tuple, auth });
    void getLocalContentEntity(auth.ownerId, 'prayer', prayer.id).then(current => {
      if (getAuthUserId() !== auth.ownerId || getAuthEpoch() !== auth.authEpoch || current?.visibleDocument) return;
      useAdiaStore.setState(state => ({ prayers: state.prayers.filter(row => row.id !== prayer.id) }));
    }).catch(() => { /* durable success; hydration repairs publication */ });
    dispatchLifecycleSync();
    return { ok: true, pending: true };
  } catch (error) {
    return {
      ok: false,
      code: error instanceof Error && error.message === 'content-sync-not-authenticated'
        ? 'not-signed-in'
        : 'try-again',
    };
  }
}

export async function restorePrayerProtected(
  snapshot: Prayer,
): Promise<ProtectedActionResult> {
  assertCoreWriteAllowed('content-lifecycle:prayer:restore');
  try {
    const ticket = undoTickets.get(snapshot);
    if (!ticket) throw new Error('prayer-restore-ticket-required');
    const context = await getCurrentContentLifecycleContext(ticket.auth);
    const restored = await authorExplicitPrayerRestore(context, snapshot, ticket.tuple);
    undoTickets.delete(snapshot);
    void getLocalContentEntity(ticket.auth.ownerId, 'prayer', snapshot.id).then(current => {
      if (!current?.visibleDocument || JSON.stringify(current.visibleDocument) !== JSON.stringify(completePrayerDocument(restored))) return;
      useAdiaStore.getState().publishPrayer(restored, ticket.auth);
    }).catch(() => { /* durable success; hydration repairs publication */ });
    dispatchLifecycleSync();
    return { ok: true, pending: true };
  } catch (error) {
    return {
      ok: false,
      code: error instanceof Error && error.message === 'content-sync-not-authenticated'
        ? 'not-signed-in'
        : 'try-again',
    };
  }
}

